// Helpers puros da LiveScreen — sem hooks, sem estado.
// Extraídos do LiveScreen.tsx pra reduzir o monolito (P0 do code review).
//
// Mantém em sync com taxonomy do backend (apps/api/data/country-taxonomy.json
// + canonical-channels.ts). Labels hardcoded BR como fallback caso a
// taxonomy do país não carregue.

import { i18n } from '@app-iptv/i18n'
import type { GroupedChannel, LiveChannel } from '@app-iptv/iptv-client'
import type { EpgProgram } from '@app-iptv/iptv-client'

import {
  Icon24h,
  IconAdult,
  IconChurch,
  IconDoc,
  IconGlobePublic,
  IconKids,
  IconMovie,
  IconMusic,
  IconNews,
  IconRadio,
  IconSports,
  IconTv,
  IconVariety,
} from '../../components/PlayerIcons'

export interface BucketInfo {
  /** bucket id ('abertos'|'esportes'|...) ou providerCategoryId */
  id: string
  /** localizado / nome do provider */
  name: string
  icon?: JSX.Element
  count: number
}

/** Labels BR hardcoded como fallback quando taxonomy do país não
 *  carregou. Backend é fonte da verdade (country-taxonomy.json) e
 *  já entrega traduzido por país. NÃO migrar pra i18n — dev fallback
 *  é PT-BR de propósito (visibilidade do erro: se vê PT em ES, sabe
 *  que taxonomy não carregou). */
export const BUCKET_LABELS: Record<string, string> = {
  abertos: 'Abertos',
  esportes: 'Esportes',
  filmes: 'Filmes',
  series: 'Séries',
  noticias: 'Notícias',
  infantil: 'Infantil',
  variedades: 'Variedades',
  documentarios: 'Documentários',
  musica: 'Música',
  religiosos: 'Religiosos',
  internacionais: 'Internacionais',
  radios: 'Rádios',
  'canais-24h': 'Canais 24h',
  adulto: 'Adulto',
  outros: 'Outros',
}

/** Mapeia bucketId → SVG inline. Tier-aware via PlayerIcons. */
export function bucketIcon(id: string): JSX.Element {
  const size = 16
  switch (id) {
    case 'abertos':
      return <IconTv size={size} />
    case 'esportes':
      return <IconSports size={size} />
    case 'filmes':
      return <IconMovie size={size} />
    case 'series':
      return <IconTv size={size} />
    case 'noticias':
      return <IconNews size={size} />
    case 'infantil':
      return <IconKids size={size} />
    case 'variedades':
      return <IconVariety size={size} />
    case 'documentarios':
      return <IconDoc size={size} />
    case 'musica':
      return <IconMusic size={size} />
    case 'religiosos':
      return <IconChurch size={size} />
    case 'internacionais':
      return <IconGlobePublic size={size} />
    case 'radios':
      return <IconRadio size={size} />
    case 'canais-24h':
      return <Icon24h size={size} />
    case 'adulto':
      return <IconAdult size={size} />
    default:
      return <IconTv size={size} />
  }
}

/** Wrap pra LiveChannel virar GroupedChannel quando o agrupamento
 *  está desligado (Settings → Reorganizar canais OFF) ou em buckets
 *  Favoritos/Recentes (cada item é uma variante específica). */
export function singleChannelGroup(ch: LiveChannel): GroupedChannel {
  return {
    key: `single::${ch.id}`,
    displayName: ch.name,
    categoryId: ch.categoryId,
    logo: ch.logo,
    epgChannelId: ch.epgChannelId,
    hasArchive: ch.hasArchive,
    archiveWindowHours: ch.archiveWindowHours,
    isAdult: ch.isAdult,
    sources: [
      {
        channelId: ch.id,
        rawName: ch.rawName ?? ch.name,
        quality: ch.quality ?? null,
        codec: ch.codec ?? null,
        language: ch.language ?? null,
        audio: null,
        tags: [],
        score: 0,
        isBackup: false,
      },
    ],
  }
}

/** Cache de `Intl.DateTimeFormat` por chave `locale|options`.
 *
 *  CONSTRUIR `Intl.DateTimeFormat` é caríssimo no Chromium antigo (Chr53 da
 *  LG): o CPU profile do guia ao vivo no device real acusou ~18% do scripting
 *  por tecla ↑/↓ em `CreateDateTimeFormat`. A causa era este helper — cada
 *  rótulo de horário reconstruía o formatador a cada render (via
 *  `toLocaleTimeString`, que instancia um `Intl.DateTimeFormat` por chamada).
 *  Como o guia re-renderiza ~2 linhas × 3 rótulos + hero por tecla, eram ~8
 *  construções por navegação.
 *
 *  O `.format(valor)` por chamada é barato; só a CONSTRUÇÃO é cara. Então
 *  memoizamos a instância por (locale, options) e reusamos. Rechaveado por
 *  locale (multilanguage pt-BR/en-US/es-419) — nunca recriado por render.
 *  `Intl.DateTimeFormat` existe desde Chr24, dentro do piso Chr38. */
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>()

function getTimeFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let fmt = timeFormatterCache.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options)
    timeFormatterCache.set(key, fmt)
  }
  return fmt
}

const HOUR_MIN_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}

/** "HH:MM" (ou "HH:MM AM/PM") no locale ATIVO do i18n, a partir de unix seconds.
 *  Reusa um `Intl.DateTimeFormat` cacheado por (locale, options) (ver
 *  `getTimeFormatter`) — sem reconstruir o formatador a cada render.
 *
 *  Antes cravava `'pt-BR'` (24h) pra TODO usuário: en-US/es-419 viam 24h em vez do
 *  seu ciclo de hora nativo (en-US/es-419 → h12 AM/PM). Lê `i18n.language` (mesmo
 *  padrão de MovieDrillView/SeriesDrillView/api.ts) — o cache é rechaveado por
 *  locale, então trocar de idioma não vaza formatador. O rótulo 12h ("10:30 PM") é
 *  mais largo que o 24h ("22:30"), mas as células (rowProgramTime/nextRowTime/hero)
 *  são `flex-shrink:0 + nowrap` ladeadas por título `flex:1 + ellipsis`: o horário
 *  renderiza inteiro e o título absorve a diferença — sem vazar/truncar em
 *  font-scale normal nem xlarge (medido no guia @1920). */
export function formatHourMin(unixSec: number): string {
  const locale = i18n.language || 'pt-BR'
  return getTimeFormatter(locale, HOUR_MIN_OPTIONS).format(new Date(unixSec * 1000))
}

/** % do tempo decorrido do programa atual (0..100). 0 quando não
 *  começou; 100 quando já terminou. Usado pra barra de progresso EPG. */
export function progressPct(now: EpgProgram | null): number {
  if (!now) return 0
  const start = now.startTs * 1000
  const end = now.endTs * 1000
  const t = Date.now()
  if (t <= start) return 0
  if (t >= end) return 100
  return ((t - start) / (end - start)) * 100
}
